import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, ArrowRight, Minus, Package, Plus } from 'lucide-react';
import axios from 'axios';

import Header from 'shared/components/Header';
import PageLoader from 'shared/components/PageLoader';
import { useLanguage } from 'shared/context/LanguageContext';
import { EQUIPMENT_STORE_ENABLED } from 'shared/lib/featureFlags';
import { useCourierAuth } from 'features/courier-auth/context/CourierAuthContext';
import { fetchTraining } from 'features/courier-training/services/courierTrainingService';
import {
  createOrder,
  fetchCatalog,
  fetchOrders,
  type EquipmentOrder,
  type EquipmentProductView,
} from 'features/courier-store/services/courierEquipmentService';

const MAX_QTY = 10;

const CourierStorePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useCourierAuth();
  const { dir } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [catalog, setCatalog] = useState<EquipmentProductView[]>([]);
  const [orders, setOrders] = useState<EquipmentOrder[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Store is hidden for now; guard direct/bookmarked access to /portal/store.
    if (!EQUIPMENT_STORE_ENABLED) {
      navigate('/portal', { replace: true });
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const training = await fetchTraining();
        if (!training.isComplete) {
          if (!cancelled) navigate('/portal/training', { replace: true });
          return;
        }
        const [cat, ord] = await Promise.all([fetchCatalog(), fetchOrders()]);
        if (!cancelled) {
          setCatalog(cat.items);
          setOrders(ord);
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          if (!cancelled) navigate('/portal', { replace: true });
          return;
        }
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const setQty = (id: string, next: number) => {
    const clamped = Math.max(0, Math.min(MAX_QTY, next));
    setQuantities((prev) => ({ ...prev, [id]: clamped }));
  };

  const cartItems = useMemo(
    () =>
      catalog
        .map((p) => ({ product: p, quantity: quantities[p.id] ?? 0 }))
        .filter((entry) => entry.quantity > 0),
    [catalog, quantities],
  );

  const totalUnits = cartItems.reduce((sum, e) => sum + e.quantity, 0);

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) return;
    setSubmitting(true);
    try {
      const created = await createOrder(
        cartItems.map((e) => ({ productId: e.product.id, quantity: e.quantity })),
        note.trim() || undefined,
      );
      setOrders((prev) => [created, ...prev]);
      setQuantities({});
      setNote('');
      toast.success(String(t('portal.store.orderSuccess')));
    } catch {
      toast.error(String(t('portal.store.orderFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const logoutLabel = String(t('portal.logout'));
  const BackArrow = dir === 'rtl' ? ArrowRight : ArrowLeft;
  const dateFmt = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const statusLabel = (status: EquipmentOrder['status']) =>
    t(`portal.store.status.${status}`);

  const shell = (children: React.ReactNode) => (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header onLogout={handleLogout} logoutLabel={logoutLabel} />
      {children}
    </div>
  );

  if (loading) return shell(<PageLoader />);

  if (errored) {
    return shell(
      <main className="flex-1 flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-sm text-gray-600">{t('portal.store.unavailable')}</p>
        <button
          type="button"
          onClick={() => navigate('/portal')}
          className="btn-outline inline-flex items-center gap-2"
        >
          <BackArrow size={16} />
          {t('portal.store.backToPortal')}
        </button>
      </main>,
    );
  }

  return shell(
    <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <button
        type="button"
        onClick={() => navigate('/portal')}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-haat-red-700 transition-colors"
      >
        <BackArrow size={16} />
        {t('portal.store.backToPortal')}
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('portal.store.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t('portal.store.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {catalog.map((product) => {
          const qty = quantities[product.id] ?? 0;
          return (
            <div
              key={product.id}
              className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4"
            >
              <div className="h-12 w-12 rounded-xl bg-haat-red-50 text-haat-red-700 flex items-center justify-center shrink-0">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={String(t(product.nameKey))}
                    className="h-full w-full object-cover rounded-xl"
                  />
                ) : (
                  <Package className="h-5 w-5" />
                )}
              </div>
              <p className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">
                {t(product.nameKey)}
              </p>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  aria-label={String(t('portal.store.decrease'))}
                  onClick={() => setQty(product.id, qty - 1)}
                  disabled={qty <= 0}
                  className="h-8 w-8 rounded-lg border border-gray-200 text-gray-600 flex items-center justify-center disabled:opacity-40 hover:bg-gray-50"
                >
                  <Minus size={14} />
                </button>
                <span className="w-6 text-center text-sm font-bold text-gray-900">
                  {qty}
                </span>
                <button
                  type="button"
                  aria-label={String(t('portal.store.increase'))}
                  onClick={() => setQty(product.id, qty + 1)}
                  disabled={qty >= MAX_QTY}
                  className="h-8 w-8 rounded-lg border border-gray-200 text-gray-600 flex items-center justify-center disabled:opacity-40 hover:bg-gray-50"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-bold text-gray-900 mb-3">
          {t('portal.store.cartTitle')}
        </h2>
        {cartItems.length === 0 ? (
          <p className="text-sm text-gray-400">{t('portal.store.cartEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-2 mb-3">
            {cartItems.map((entry) => (
              <li
                key={entry.product.id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-700">{t(entry.product.nameKey)}</span>
                <span className="font-semibold text-gray-900">
                  ×{entry.quantity}
                </span>
              </li>
            ))}
          </ul>
        )}

        <label className="block text-xs font-semibold text-gray-600 mb-1 mt-2">
          {t('portal.store.noteLabel')}
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder={String(t('portal.store.notePlaceholder'))}
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-haat-red-200"
        />

        <button
          type="button"
          onClick={handlePlaceOrder}
          disabled={cartItems.length === 0 || submitting}
          className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting
            ? t('portal.store.placing')
            : t('portal.store.placeOrder', { count: totalUnits })}
        </button>
      </div>

      <section>
        <h2 className="text-sm font-bold text-gray-900 mb-3">
          {t('portal.store.pastOrders')}
        </h2>
        {orders.length === 0 ? (
          <p className="text-sm text-gray-400">{t('portal.store.noOrders')}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {orders.map((order) => (
              <li
                key={order.id}
                className="rounded-xl border border-gray-200 bg-white p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">
                    {dateFmt(order.createdAt)}
                  </span>
                  <span className="text-xs font-semibold rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">
                    {statusLabel(order.status)}
                  </span>
                </div>
                <ul className="flex flex-col gap-1">
                  {order.items.map((item) => (
                    <li
                      key={item.productId}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-700">{item.name}</span>
                      <span className="font-semibold text-gray-900">
                        ×{item.quantity}
                      </span>
                    </li>
                  ))}
                </ul>
                {order.note && (
                  <p className="mt-2 text-xs text-gray-500 italic">
                    {order.note}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>,
  );
};

export default CourierStorePage;
